import { convertLanguage } from '@frontend/common';
import { TextInput } from '@frontend/components/basic-forms';
import { CommonMessage, getCorrectLocale } from '@frontend/lang';
import { CreateProduct } from '@frontend/product/types';
import { useState } from 'react';
import { LuChevronDown, LuChevronRight } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { useWizardModalScreenNavigation } from '../../wizard-modal-navigation.context';
import useEnterProductTranslations, { ProductTranslationSectionKey } from './enter-product-translations.controller';

const ID = 'wizard-enter-product-translations';

const getLanguageFromTranslationField = (field: keyof CreateProduct) => {
    const parts = field.split('_');
    const language = parts.at(-2);
    return language ? convertLanguage(getCorrectLocale(language)) : field;
};

const getSectionTitle = (section: 'name' | 'description' | 'user_info') => {
    switch (section) {
        case 'name':
            return CommonMessage.OBJECTS.COMMON_FIELDS.NAME;
        case 'description':
            return CommonMessage.OBJECTS.COMMON_FIELDS.DESCRIPTION;
        case 'user_info':
            return CommonMessage.OBJECTS.PRODUCT.USER_INFO;
    }
};

const EnterProductTranslations = (props: { wizard_id: string }) => {
    const viewProps = useEnterProductTranslations(props);
    const [expandedSections, setExpandedSections] = useState<Record<ProductTranslationSectionKey, boolean>>({
        name: false,
        description: false,
        user_info: false
    });

    useWizardModalScreenNavigation({
        onContinue: viewProps.onSubmit,
        continueDisabled: false
    });

    const toggleSection = (section: ProductTranslationSectionKey) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    return (
        <div
            id={ID}
            className='flex w-full flex-col gap-4'>
            <div className='text-foreground-default text-base font-medium'>
                <FormattedMessage
                    id='platform.wizard.enter-product-translations.heading'
                    description='Heading shown on the platform create product wizard product translations screen'
                    defaultMessage='Enter product translations'
                />
            </div>

            {viewProps.translationSections.map((section) => (
                <div
                    className='outline-border-default flex flex-col gap-3 rounded-xl p-4 outline-1'
                    key={section.key}>
                    <button
                        type='button'
                        className='flex w-full cursor-pointer items-center justify-between gap-3 text-left'
                        aria-expanded={expandedSections[section.key]}
                        aria-controls={`${ID}-${section.key}-fields`}
                        onClick={() => toggleSection(section.key)}>
                        <div className='text-foreground-default text-base font-semibold'>{getSectionTitle(section.key)}</div>
                        {expandedSections[section.key] ? (
                            <LuChevronDown className='text-brand-muted size-5 shrink-0' />
                        ) : (
                            <LuChevronRight className='text-brand-muted size-5 shrink-0' />
                        )}
                    </button>

                    {expandedSections[section.key] && (
                        <div
                            id={`${ID}-${section.key}-fields`}
                            className='flex flex-col gap-3'>
                            {section.fields.map((field) => (
                                <TextInput
                                    id={`${ID}-${field}`}
                                    key={field}
                                    label={getLanguageFromTranslationField(field)}
                                    value={viewProps.product[field] as string | null | undefined}
                                    onChange={(value) => viewProps.onTranslationChange(section.key, field, value)}
                                    className='w-full'
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default EnterProductTranslations;
